import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Grid, ThemeConstellation, VisuallyHidden } from '@zillow/constellation';
import { propertyUtils } from '@zg-rentals/rental-manager-env';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../Wizard/context';

import ListingTourFormField from '../ListingTour/components/ListingTourFormField';
import PageWizard from '../../Wizard/PageWizard';
import WizardAlert from '../../WizardAlert';
import WizardTipCardContainer from '../../WizardTipCardContainer';
import PhotoUploadFormField from './components/PhotoUploadFormField';
import WizardTipContent from './components/WizardTipContent';

import { StyledDivCustomLeftSpacing } from '../../StyledComponents/StyledWizardForm';
import { WizardBody, WizardForm, WizardFormSectionContainer, WizardHeading } from '../../../styles/wizard';

const propTypes = {
  onGoToPrevStep: PropTypes.func.isRequired,
  onGoToNextStep: PropTypes.func.isRequired,
  wizardTitle: PropTypes.node.isRequired,
  progressStepper: PropTypes.node.isRequired,
  totalSteps: PropTypes.number.isRequired,
  currentStepIndex: PropTypes.number.isRequired,
  showDesktopViewBreakpoint: PropTypes.oneOf(Object.keys(ThemeConstellation.ns().breakpoints)).isRequired,
};

const defaultProps = {};

const AddPhotos = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}) => {
  const {
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const currentProperty = useSelector((state) => state.properties.currentProperty);

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const heading = isRoomForRent ? 'Add photos of the room and property' : 'Add photos';
  const tipCardHeading = isRoomForRent ? 'Add photos of the room' : 'Add photos';

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  return (
    <PageWizard
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div className="AddPhotos">
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <Grid
          display="grid"
          gridTemplateColumns={{
            default: 'repeat(6, 1fr)',
            // 769
            lg_gte: 'repeat(12, 1fr)',
          }}
          gap="sm"
        >
          <Grid gridColumnEnd={{ default: 'span 6', lg_gte: 'span 12', xl_gte: 'span 7', xxl_gte: 'span 7' }}>
            {/* cast as div to prevent cannot appear as a descendant of form error */}
            <WizardForm asChild={true} onSubmit={(e) => e.preventDefault()}>
              <div>
                <VisuallyHidden>
                  <h1>Media</h1>
                </VisuallyHidden>

                <WizardFormSectionContainer>
                  <WizardHeading level={1} textStyle="heading-sm">
                    {heading}
                  </WizardHeading>

                  <WizardBody>Photos help renters imagine living in your place.</WizardBody>

                  <PhotoUploadFormField />
                </WizardFormSectionContainer>

                <WizardFormSectionContainer>
                  <WizardHeading level={2} textStyle="heading-sm">
                    3D Tour
                  </WizardHeading>
                  <ListingTourFormField />
                </WizardFormSectionContainer>
              </div>
            </WizardForm>
          </Grid>

          {/* tip component */}
          <Grid gridColumnEnd={{ default: 'span 6', lg_gte: 'span 12', xl_gte: 'span 12', xxl_gte: 'span 5' }}>
            <StyledDivCustomLeftSpacing>
              <WizardTipCardContainer header={tipCardHeading} showDesktopViewBreakpoint={showDesktopViewBreakpoint}>
                <WizardTipContent />
              </WizardTipCardContainer>
            </StyledDivCustomLeftSpacing>
          </Grid>
        </Grid>
      </div>
    </PageWizard>
  );
};

AddPhotos.propTypes = propTypes;
AddPhotos.defaultProps = defaultProps;

export default AddPhotos;
